<template>
  <BContainer
    fluid
    class="mb-4"
  >
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5
        class="fw-bold mb-0"
        style="color: #1e293b;"
      >
        My Complaints
      </h5>
      <BButton
        variant="primary"
        size="sm"
        @click="openFormModal"
      >
        <i class="fas fa-plus me-1" />
        File Complaint
      </BButton>
    </div>

    <BModal
      v-model="showForm"
      title="File a Complaint"
      size="lg"
      modal-class="student-form-modal"
      centered
      hide-footer
      @hidden="onModalHidden"
    >
      <BForm @submit.prevent="submitComplaint">
        <BRow>
          <BCol
            cols="12"
            md="6"
          >
            <BFormGroup
              label="Title"
              label-for="complaint-title"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormInput
                id="complaint-title"
                v-model.trim="form.title"
                maxlength="255"
                :disabled="state.submitting"
                placeholder="Short complaint title"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
            md="6"
          >
            <BFormGroup
              label="Type"
              label-for="complaint-type"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormSelect
                id="complaint-type"
                v-model="form.complaint_type"
                :disabled="state.submitting"
                :options="complaintTypeOptions"
              />
            </BFormGroup>
          </BCol>

          <BCol
            v-if="form.complaint_type === 'other'"
            cols="12"
            md="6"
          >
            <BFormGroup
              label="Specify Type"
              label-for="other-type"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormInput
                id="other-type"
                v-model.trim="form.other_type"
                maxlength="255"
                :disabled="state.submitting"
                placeholder="Enter custom complaint type"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
            md="6"
          >
            <BFormGroup
              label="Reported Person (Optional)"
              label-for="reported-person-name"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormInput
                id="reported-person-name"
                v-model.trim="form.reported_person_name"
                maxlength="255"
                :disabled="state.submitting"
                placeholder="Name"
              />
            </BFormGroup>
          </BCol>

          <BCol
            cols="12"
            md="6"
          >
            <BFormGroup
              label="Reported Person Role (Optional)"
              label-for="reported-person-role"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormInput
                id="reported-person-role"
                v-model.trim="form.reported_person_role"
                maxlength="255"
                :disabled="state.submitting"
                placeholder="Role or position"
              />
            </BFormGroup>
          </BCol>

          <BCol cols="12">
            <BFormGroup
              label="Description"
              label-for="complaint-description"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormTextarea
                id="complaint-description"
                v-model.trim="form.description"
                rows="4"
                maxlength="5000"
                :disabled="state.submitting"
                placeholder="Describe your complaint in detail"
              />
            </BFormGroup>
          </BCol>

          <BCol cols="12">
            <BFormGroup
              label="Attachment (Optional)"
              label-for="complaint-attachment"
              label-class="font-weight-bold"
              class="mb-2"
            >
              <BFormFile
                id="complaint-attachment"
                v-model="form.attachment"
                :disabled="state.submitting"
                accept=".pdf,.jpeg,.jpg,.png,.doc,.docx"
                placeholder="Choose a file..."
                drop-placeholder="Drop file here..."
              />
              <small class="text-muted">Allowed: PDF, JPG, PNG, DOC, DOCX (max 10MB)</small>
            </BFormGroup>
          </BCol>
        </BRow>

        <div class="d-flex justify-content-end mt-3">
          <BButton
            variant="outline-secondary"
            class="me-2"
            :disabled="state.submitting"
            @click="showForm = false"
          >
            Cancel
          </BButton>
          <BButton
            type="submit"
            variant="primary"
            :disabled="state.submitting"
          >
            <i :class="['fas me-1', state.submitting ? 'fa-spinner fa-spin' : 'fa-paper-plane']" />
            {{ state.submitting ? 'Submitting...' : 'Submit Complaint' }}
          </BButton>
        </div>
      </BForm>
    </BModal>

    <BCard class="border shadow-sm">
      <BCardText>
        <BOverlay
          :show="state.loading"
          opacity="0.5"
        >
          <BTable
            :items="complaints"
            :fields="tableFields"
            responsive
            stacked="md"
            show-empty
            hover
            class="mb-0"
          >
            <template #empty>
              <div class="text-center py-4">
                <i
                  class="fas fa-inbox mb-2"
                  style="font-size: 2rem; color: #cbd5e1;"
                />
                <p
                  class="small mb-0"
                  style="color: #94a3b8;"
                >
                  No complaints filed yet
                </p>
              </div>
            </template>

            <template #cell(status)="{ value }">
              <BBadge :variant="statusVariant(value)">
                {{ value }}
              </BBadge>
            </template>

            <template #cell(date_reported)="{ value }">
              {{ formatDate(value) }}
            </template>

            <template #cell(actions)="{ item }">
              <BButton
                v-if="item.status === 'pending'"
                size="sm"
                variant="outline-danger"
                @click="withdrawComplaint(item)"
              >
                Withdraw
              </BButton>
            </template>
          </BTable>
        </BOverlay>

        <div
          v-if="pagination.last_page > 1"
          class="d-flex justify-content-end mt-3"
        >
          <BPagination
            v-model="pagination.current_page"
            :total-rows="pagination.total"
            :per-page="pagination.per_page"
            size="sm"
            @change="onPageChange"
          />
        </div>
      </BCardText>
    </BCard>
  </BContainer>
</template>

<script>
import MxMisc from '@/mixins/misc'
import { SStudentComplaint } from '@/services/student'

const defaultForm = () => ({
  title: '',
  description: '',
  complaint_type: 'complaint',
  other_type: '',
  reported_person_name: '',
  reported_person_role: '',
  attachment: null
})

export default {
  name: 'StudentComplaints',

  mixins: [MxMisc],

  middleware: ['auth', 'student'],

  metaInfo: () => ({ title: 'My Complaints' }),

  data () {
    return {
      showForm: false,
      form: defaultForm(),
      complaints: [],
      pagination: { current_page: 1, per_page: 10, total: 0, last_page: 1 },
      state: {
        loading: false,
        submitting: false
      }
    }
  },

  computed: {
    complaintTypeOptions () {
      return [
        { value: 'complaint', text: 'Complaint' },
        { value: 'issue', text: 'Issue' },
        { value: 'other', text: 'Other' }
      ]
    },

    tableFields () {
      return [
        { key: 'title', label: 'Title' },
        { key: 'complaint_type', label: 'Type' },
        { key: 'status', label: 'Status' },
        { key: 'date_reported', label: 'Date Reported' },
        { key: 'actions', label: 'Actions' }
      ]
    }
  },

  mounted () {
    this.fetchComplaints()
  },

  methods: {
    openFormModal () {
      this.showForm = true
    },

    onModalHidden () {
      this.form = defaultForm()
    },

    formatDate (value) {
      if (!value) return '—'
      return new Date(value).toLocaleString()
    },

    statusVariant (status) {
      const variants = {
        pending: 'warning',
        in_review: 'info',
        resolved: 'success',
        dismissed: 'secondary'
      }
      return variants[status] || 'secondary'
    },

    onPageChange (page) {
      this.pagination.current_page = page
      this.fetchComplaints()
    },

    async fetchComplaints () {
      this.state.loading = true
      try {
        const { data: payload } = await SStudentComplaint.myComplaints({
          page: this.pagination.current_page,
          per_page: this.pagination.per_page
        })
        const data = payload?.data || payload

        this.complaints = data?.data || []
        if (data?.pagination) {
          this.pagination = { ...this.pagination, ...data.pagination }
        }
      } catch {
        this.complaints = []
      } finally {
        this.state.loading = false
      }
    },

    async submitComplaint () {
      if (!this.form.title || !this.form.description || !this.form.complaint_type) {
        this.swalInvalid('Please complete all required fields.')
        return
      }

      if (this.form.complaint_type === 'other' && !this.form.other_type) {
        this.swalInvalid('Please specify the complaint type.')
        return
      }

      this.state.submitting = true
      try {
        const payload = {
          title: this.form.title,
          description: this.form.description,
          complaint_type: this.form.complaint_type,
          other_type: this.form.complaint_type === 'other' ? this.form.other_type : null,
          reported_person_name: this.form.reported_person_name || null,
          reported_person_role: this.form.reported_person_role || null
        }

        const { data: responsePayload } = await SStudentComplaint.file(payload)
        const response = responsePayload?.data || responsePayload
        const complaintId = response?.data?.id || response?.id

        if (complaintId && this.form.attachment) {
          await SStudentComplaint.uploadAttachment(complaintId, this.form.attachment)
        }

        this.swalSuccess('Complaint submitted successfully.')
        this.showForm = false
        this.form = defaultForm()
        this.fetchComplaints()
      } catch (error) {
        const message = error?.message?.message || error?.response?.data?.message || 'Failed to submit complaint.'
        this.swalInvalid(message)
      } finally {
        this.state.submitting = false
      }
    },

    withdrawComplaint (item) {
      this.swalConfirm({
        html: '<h6>Withdraw this complaint?</h6><p class="small text-muted">Only pending complaints can be withdrawn.</p>',
        confirmButtonText: 'Withdraw',
        preConfirm: () => {
          return SStudentComplaint.withdraw(item.id).then(() => {
            this.swalSuccess('Complaint withdrawn successfully.')
            this.fetchComplaints()
          }).catch(({ message }) => {
            this.swalInvalid(message?.message || 'Failed to withdraw complaint.')
          })
        }
      })
    }
  }
}
</script>

<style>
.student-form-modal .modal-header {
  border-bottom: 1px solid #e2e8f0;
}

.student-form-modal .modal-footer {
  border-top: 1px solid #e2e8f0;
}

@media (max-width: 575.98px) {
  .student-form-modal .modal-dialog {
    margin: 0;
    max-width: 100%;
    min-height: 100vh;
  }

  .student-form-modal .modal-content {
    min-height: 100vh;
    border-radius: 0;
  }
}
</style>
